import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { NotificationType } from "@transfr-inc/dashboard-components";
import clsx from "clsx";
import PropTypes from "prop-types";
import { useState } from "react";

import { useContainer } from "@contexts/container-context";
import { useNotificationContext } from "@contexts/notification-context";
import { ZeroStateMessage } from "@src/views/shared";

import { DocumentsAccordion } from "@views/curriculum-resources/components";
import {
  filterDocuments,
  getVisibleItemIdsFromData,
} from "@views/curriculum-resources/utils";
import { CurriculumResourcesToolbar } from "./curriculum-resources.toolbar";

import "./curriculum-resources.product.scss";

const getDocumentsLevelTitle = (data) => {
  // Assuming dataset level has the same key value
  const levelKey = data?.[0]?.key?.trim() || "folder";
  return `${levelKey}s`;
};

export const CurriculumResourcesProduct = ({
  data,
  className,
  loading,
  product,
  tracking,
  organizationCode,
}) => {
  const { documentsService } = useContainer();
  const { addNotification } = useNotificationContext();

  const [documents, setDocuments] = useState(data);
  const [hasActiveFilters, setHasActiveFilters] = useState(false);
  const [downloadingLevelId, setDownloadingLevelId] = useState(null);
  const [isDownloadingAll, setIsDownloadingAll] = useState(false);

  const onFiltersChange = (filters) => {
    const filtered = filterDocuments(data, filters);
    setDocuments(filtered);
    setHasActiveFilters(Object.values(filters).some((v) => v && v !== ""));
  };

  const handleDownload = async (levelId, itemIds) => {
    setDownloadingLevelId(levelId);
    try {
      await documentsService.downloadDocuments({
        organizationCode,
        productId: product.productId,
        levelId,
        itemIds,
      });
    } catch {
      addNotification({
        type: NotificationType.error,
        message: "Download failed. Please try again.",
      });
    } finally {
      setDownloadingLevelId(null);
    }
  };

  const handleDownloadAll = async () => {
    setIsDownloadingAll(true);
    try {
      const response = await documentsService.requestDocumentsDownload({
        productId: product.productId,
        itemIds: hasActiveFilters ? getVisibleItemIdsFromData(documents) : null,
        organizationCode,
      });
      addNotification({
        type: NotificationType.success,
        message: response.message,
      });
    } catch {
      addNotification({
        type: NotificationType.error,
        message: "Download request failed. Please try again.",
      });
    } finally {
      setIsDownloadingAll(false);
    }
  };

  return (
    <div className={clsx("curriculum-resources-product", className)}>
      {data?.length > 0 ? (
        <>
          <CurriculumResourcesToolbar
            data={data}
            onFiltersChange={onFiltersChange}
            onDownloadAll={handleDownloadAll}
            isDownloadingAll={isDownloadingAll}
            hasDocuments={documents?.some(
              (node) =>
                (node.items?.length ?? 0) > 0 ||
                (node.children?.length ?? 0) > 0
            )}
            product={product}
            tracking={tracking}
          />
          {documents?.length > 0 ? (
            documents.map((resource) => (
              <DocumentsAccordion
                key={resource.id}
                data={resource}
                levelTitle={getDocumentsLevelTitle(resource.children)}
                product={product}
                tracking={tracking}
                onDownload={handleDownload}
                downloadingLevelId={downloadingLevelId}
                hasActiveFilters={hasActiveFilters}
              />
            ))
          ) : (
            <ZeroStateMessage
              message="No resources found with the applied filters."
              icon={<FontAwesomeIcon icon={["fa-solid", "ban"]} />}
            />
          )}
        </>
      ) : (
        <ZeroStateMessage
          message={loading ? "Loading..." : "No resources found."}
          icon={
            <FontAwesomeIcon
              icon={loading ? ["fa-solid", "spinner"] : ["fa-solid", "ban"]}
              spin={loading}
            />
          }
        />
      )}
    </div>
  );
};

CurriculumResourcesProduct.propTypes = {
  className: PropTypes.string,
  data: PropTypes.array.isRequired,
  loading: PropTypes.bool,
  product: PropTypes.object,
  tracking: PropTypes.object,
};
